import clsx from "clsx";
import { CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/20/solid";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import type { DokumentSaveState } from "@/pages/dokumente/useDokumentAutoSave";

interface DocumentSaveStatusProps {
  saveState: DokumentSaveState;
  hasUnsavedChanges: boolean;
  lastSavedAt: Date | null;
  saveError: string | null;
  onRetry: () => void;
  retrying?: boolean;
}

function formatSavedTime(value: Date): string {
  return value.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

export function DocumentSaveStatus({
  saveState,
  hasUnsavedChanges,
  lastSavedAt,
  saveError,
  onRetry,
  retrying = false,
}: DocumentSaveStatusProps) {
  const isSaving = saveState === "pending" || saveState === "saving" || retrying;
  const showSaved = !saveError && !isSaving && !hasUnsavedChanges && lastSavedAt != null;
  const showError = Boolean(saveError) && !isSaving;
  const visible = showSaved || isSaving || showError;

  // Immer gleiche Höhe reservieren – sonst springt Header/Canvas bei jedem Edit/Save.
  return (
    <div className="flex min-h-[2.25rem] justify-end" role="status" aria-live="polite">
      {visible ? (
        <div
          className={clsx(
            "inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1.5 text-xs shadow-sm backdrop-blur-sm transition-colors",
            showError &&
              "border-status-red/25 bg-status-red/[0.06] text-status-red dark:bg-status-red/10",
            isSaving &&
              "border-accent/20 bg-accent/[0.06] text-label-secondary dark:bg-accent/10",
            showSaved &&
              "border-status-green/20 bg-status-green/[0.05] text-label-secondary dark:bg-status-green/10",
          )}
        >
          {showError ? (
            <>
              <ExclamationCircleIcon className="h-4 w-4 shrink-0 text-status-red" aria-hidden />
              <div className="min-w-0 max-w-[min(22rem,55vw)] text-left" title={saveError ?? undefined}>
                <p className="font-medium text-status-red">Speichern fehlgeschlagen</p>
                <p className="line-clamp-2 text-[11px] leading-snug opacity-90">{saveError}</p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="ml-1 shrink-0 !py-1 !text-xs"
                onClick={onRetry}
                loading={retrying}
              >
                <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden />
                Erneut
              </Button>
            </>
          ) : null}

          {isSaving ? (
            <>
              <Spinner className="h-3.5 w-3.5 text-accent" />
              <span className="font-medium text-label">
                {saveState === "pending" ? "Änderungen werden gespeichert …" : "Entwurf wird gespeichert …"}
              </span>
            </>
          ) : null}

          {showSaved ? (
            <>
              <CheckCircleIcon className="h-4 w-4 shrink-0 text-status-green" aria-hidden />
              <span className="font-medium text-label">Entwurf gespeichert</span>
              <span className="text-label-tertiary" aria-hidden>
                ·
              </span>
              <time dateTime={lastSavedAt.toISOString()} className="tabular-nums text-label-secondary">
                {formatSavedTime(lastSavedAt)}
              </time>
            </>
          ) : null}
        </div>
      ) : (
        <div
          className="invisible inline-flex items-center gap-2 px-3 py-1.5 text-xs"
          aria-hidden
        >
          <span className="font-medium">Entwurf gespeichert</span>
        </div>
      )}
    </div>
  );
}
